import { useEffect, useRef } from 'react';

/**
 * Custom hook to detect clicks outside a referenced element.
 *
 * Key design decisions:
 * - Uses a callbackRef (not useCallback) so callers can always pass inline
 *   arrow functions without causing the effect to re-run or the listener to
 *   reset. The ref always holds the latest callback, avoiding stale closures.
 * - Uses bubble phase (capture:false) so buttons inside the dropdown fire
 *   their own click handlers BEFORE this listener can unmount the dropdown.
 * - Dual-header shells must point this ref at the *visible* wrapper via
 *   `useBreakpointBoundRef` — a hidden sibling winning the ref makes Sign out
 *   look unclickable (mousedown/touchstart dismisses, click never fires).
 * - Only registers listeners while `enabled` is true (saves resources when
 *   the menu is closed).
 * - Handles both mouse and touch for full device support.
 *
 * @param {Function} callback - Called when a click/touch outside is detected
 * @param {boolean}  enabled  - Activate listener only when true (e.g. isOpen)
 * @returns {React.RefObject} Attach to the container element you want to watch
 *
 * @example
 * const [isOpen, setIsOpen] = useState(false);
 * const menuRef = useClickOutside(() => setIsOpen(false), isOpen);
 * return <div ref={menuRef}>…</div>;
 */
export const useClickOutside = (callback, enabled = true) => {
  const containerRef = useRef(null);

  // Store the latest callback in a ref so the effect never needs to re-run
  // when the caller passes a new inline function — no stale closure risk.
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return;

    const handleOutsideInteraction = (event) => {
      if (!event.target || !(event.target instanceof Node)) return;
      if (!containerRef.current) return;
      if (containerRef.current.contains(event.target)) return;

      try {
        callbackRef.current(event);
      } catch (error) {
        console.error('useClickOutside: error in callback', error);
      }
    };

    // Bubble phase (capture:false): the button's own click fires first,
    // THEN this listener runs. Prevents the dropdown from being torn down
    // before the button inside it can execute its handler.
    const options = { capture: false, passive: true };

    document.addEventListener('mousedown', handleOutsideInteraction, options);
    document.addEventListener('touchstart', handleOutsideInteraction, options);

    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction, options);
      document.removeEventListener('touchstart', handleOutsideInteraction, options);
    };
  }, [enabled]); // callback deliberately excluded — callbackRef handles freshness

  return containerRef;
};
